import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Game } from '../core/game';
import type { NetworkManager } from '../network/networkManager';

export class NetworkOSD {
  container: Container;
  private background: Graphics;
  private textElements: Map<string, Text> = new Map();
  private game: Game;
  private networkManager: NetworkManager | null = null;
  private lastUpdate: number = 0;
  private readonly UPDATE_THROTTLE = 250;
  private visible: boolean = false;

  constructor(game: Game, screenWidth: number, _screenHeight: number) {
    this.game = game;
    this.container = new Container();
    this.container.visible = false;

    const padding = 10;
    const osdWidth = 240;
    const osdHeight = 180;
    const x = screenWidth - osdWidth - padding;
    const y = padding;

    this.background = new Graphics();
    this.background.roundRect(x, y, osdWidth, osdHeight, 8);
    this.background.fill({ color: 0x000000, alpha: 0.7 });
    this.background.stroke({ width: 2, color: 0x444444 });
    this.container.addChild(this.background);

    const textStyle = new TextStyle({
      fontFamily: 'Courier New, monospace',
      fontSize: 12,
      fill: '#ffffff',
    });

    const labels = ['Status:', 'Conn:', 'Peers:', 'RTT:', 'Turn:', 'Phase:', 'Tick:', 'Resync:'];

    let currentY = y + 12;
    labels.forEach((label) => {
      const labelText = new Text({ text: label, style: textStyle });
      labelText.position.set(x + 10, currentY);
      this.container.addChild(labelText);

      const valueText = new Text({ text: '-', style: textStyle });
      valueText.position.set(x + 80, currentY);
      this.textElements.set(label, valueText);
      this.container.addChild(valueText);

      currentY += 22;
    });
  }

  setNetworkManager(nm: NetworkManager): void {
    this.networkManager = nm;
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  update(): void {
    if (!this.visible) return;

    const now = performance.now();
    if (now - this.lastUpdate < this.UPDATE_THROTTLE) {
      return;
    }
    this.lastUpdate = now;

    this.setText('Status:', this.getConnectionStatus());
    this.setText('Conn:', this.getConnectionType());
    this.setText('Peers:', this.getPeerCount());
    this.setText('RTT:', this.getRTT());
    this.setText('Turn:', this.getTurnOwner());
    this.setText('Phase:', this.getPhase());
    this.setText('Tick:', this.getGameTick());
    this.setText('Resync:', this.getResyncStatus());
  }

  private setText(label: string, value: string): void {
    const el = this.textElements.get(label);
    if (el) el.text = value;
  }

  private getConnectionStatus(): string {
    if (!this.networkManager) return 'Offline';
    return this.networkManager.getConnectionState();
  }

  private getConnectionType(): string {
    if (!this.networkManager) return '-';
    const info = this.networkManager.getConnectionInfo();
    if (!info) return 'pending...';
    return info.type;
  }

  private getPeerCount(): string {
    if (!this.networkManager) return '-';
    return String(this.networkManager.getPeerCount());
  }

  private getTurnOwner(): string {
    const state = (this.game as any).state;
    if (!state) return '-';

    const currentTeam = state.getCurrentTeam();
    if (!currentTeam) return '-';

    return currentTeam.name || `Team ${state.currentTeamIndex + 1}`;
  }

  private getPhase(): string {
    const state = (this.game as any).state;
    if (!state || !state.phase) return '-';
    return state.phase;
  }

  private getGameTick(): string {
    const tick = (this.game as any).gameTick;
    return tick !== undefined ? tick.toString() : '-';
  }

  private getRTT(): string {
    if (!this.networkManager) return '-';
    const rtt = this.networkManager.getAverageRTT();
    if (rtt === 0) return '-';
    const quality = this.networkManager.getConnectionQuality();
    const qualitySymbol =
      quality === 'good' ? '✓' : quality === 'degraded' ? '~' : quality === 'poor' ? '!' : '✗';
    return `${Math.round(rtt)}ms ${qualitySymbol}`;
  }

  private getResyncStatus(): string {
    const resyncManager = (this.game as any).resyncManager;
    if (!resyncManager) return '-';
    return resyncManager.isActive() ? 'RESYNCING' : 'OK';
  }
}
